import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export const UploadStates = [
  'queued',
  'uploading',
  'processing',
  'uploaded',
  'failed',
] as const;

export type UploadState = typeof UploadStates[number];

interface UploadCompletionOptions {
  retainUntilBatchSettled?: boolean;
  destinationPath?: string;
}

interface UploadBatchSettlementOptions {
  completedRemovalDelayMs?: number;
  resourceId?: string;
}

export interface FileInfo {
  id: string;
  name: string;
  size: number;
  src?: string;
  batchId: string;
  bytesUploaded: number;
  rotation?: number;
  status: UploadState;
  type: 'video' | 'image';
  resourceId?: string; // Story, Clip, or Media Library source Id
  destinationPath?: string;
  canDismissFailure?: boolean;
}

interface UploadActions {
  addFiles: (files: FileInfo[]) => void;
  dismissFailedUpload: (id: string) => void;
  setUploadStarted: (id: string) => void;
  setUploadProgress: (id: string, bytesUploaded: number) => void;
  setUploadProcessing: (id: string) => void;
  setUploadCompleted: (id: string, options?: UploadCompletionOptions) => void;
  setUploadFailed: (id: string) => void;
  settleUploadBatch: (
    batchId: string,
    options?: UploadBatchSettlementOptions
  ) => void;
}

interface UploadsState extends UploadActions {
  files: readonly FileInfo[];
}

type Mutator = (fileInfo: FileInfo) => FileInfo;

export const UploadsContext = createContext<UploadsState | null>(null);
const UploadActionsContext = createContext<UploadActions | null>(null);
const ActiveUploadResourcesContext = createContext<ReadonlySet<string> | null>(
  null
);
const UploadResourceRevisionsContext = createContext<ReadonlyMap<
  string,
  number
> | null>(null);

const activeUploadStates: UploadState[] = ['queued', 'uploading', 'processing'];

const isActiveUpload = (fileInfo: FileInfo) =>
  activeUploadStates.includes(fileInfo.status);

const UploadsContextProvider: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [uploadResourceRevisions, setUploadResourceRevisions] = useState<
    ReadonlyMap<string, number>
  >(new Map());
  const removalTimeouts = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(
    () => () => {
      removalTimeouts.current.forEach(timeout => clearTimeout(timeout));
    },
    []
  );

  const addFiles = useCallback((newFiles: readonly FileInfo[]) => {
    setFiles(oldFiles => [...oldFiles, ...newFiles]);
  }, []);

  const dismissFailedUpload = useCallback((id: string) => {
    setFiles(oldFiles => {
      const failedFile = oldFiles.find(
        fileInfo =>
          fileInfo.id === id &&
          fileInfo.status === 'failed' &&
          fileInfo.canDismissFailure
      );

      if (!failedFile) {
        return oldFiles;
      }

      URL.revokeObjectURL(failedFile.id);
      return oldFiles.filter(fileInfo => fileInfo.id !== id);
    });
  }, []);

  const mutateFileInfo = useCallback((id: string, mutator: Mutator) => {
    setFiles(oldFiles =>
      oldFiles.map(file => (file.id === id ? mutator(file) : file))
    );
  }, []);

  const setUploadProgress = useCallback(
    (id: string, bytesUploaded: number) => {
      const mutator: Mutator = fileInfo => ({
        ...fileInfo,
        bytesUploaded,
      });
      return mutateFileInfo(id, mutator);
    },
    [mutateFileInfo]
  );

  const setUploadState = useCallback(
    (id: string, status: UploadState) => {
      const mutator: Mutator = fileInfo => ({
        ...fileInfo,
        status,
      });
      return mutateFileInfo(id, mutator);
    },
    [mutateFileInfo]
  );

  const removeCompletedFilesAfterDelay = useCallback(
    (matchesFile: (fileInfo: FileInfo) => boolean, removalDelayMs = 3000) => {
      const timeout = setTimeout(() => {
        setFiles(oldFiles => {
          const filesToRemove = oldFiles.filter(
            fileInfo => matchesFile(fileInfo) && fileInfo.status === 'uploaded'
          );

          filesToRemove.forEach(fileInfo => {
            URL.revokeObjectURL(fileInfo.id);
          });

          return oldFiles.filter(
            fileInfo => !matchesFile(fileInfo) || fileInfo.status !== 'uploaded'
          );
        });
        removalTimeouts.current.delete(timeout);
      }, removalDelayMs);

      removalTimeouts.current.add(timeout);
    },
    []
  );

  const setUploadStarted = useCallback(
    (id: string) => {
      setUploadState(id, 'uploading');
    },
    [setUploadState]
  );

  const setUploadProcessing = useCallback(
    (id: string) => {
      const mutator: Mutator = fileInfo => ({
        ...fileInfo,
        bytesUploaded: fileInfo.size,
        status: 'processing',
      });
      mutateFileInfo(id, mutator);
    },
    [mutateFileInfo]
  );

  const setUploadCompleted = useCallback(
    (id: string, options?: UploadCompletionOptions) => {
      const mutator: Mutator = fileInfo => ({
        ...fileInfo,
        bytesUploaded: fileInfo.size,
        status: 'uploaded',
        ...(options?.destinationPath && {
          destinationPath: options.destinationPath,
        }),
      });
      mutateFileInfo(id, mutator);

      if (!options?.retainUntilBatchSettled) {
        removeCompletedFilesAfterDelay(fileInfo => fileInfo.id === id);
      }
    },
    [mutateFileInfo, removeCompletedFilesAfterDelay]
  );

  const setUploadFailed = useCallback(
    (id: string) => {
      setUploadState(id, 'failed');
    },
    [setUploadState]
  );

  const settleUploadBatch = useCallback(
    (batchId: string, options?: UploadBatchSettlementOptions) => {
      const resourceId = options?.resourceId;
      if (resourceId) {
        setUploadResourceRevisions(currentRevisions => {
          const nextRevisions = new Map(currentRevisions);
          nextRevisions.set(
            resourceId,
            (currentRevisions.get(resourceId) ?? 0) + 1
          );
          return nextRevisions;
        });
      }

      removeCompletedFilesAfterDelay(
        fileInfo => fileInfo.batchId === batchId,
        options?.completedRemovalDelayMs
      );
    },
    [removeCompletedFilesAfterDelay]
  );

  const uploadActions = useMemo<UploadActions>(
    () => ({
      addFiles,
      dismissFailedUpload,
      setUploadStarted,
      setUploadProgress,
      setUploadProcessing,
      setUploadCompleted,
      setUploadFailed,
      settleUploadBatch,
    }),
    [
      addFiles,
      dismissFailedUpload,
      setUploadCompleted,
      setUploadFailed,
      setUploadProcessing,
      setUploadProgress,
      setUploadStarted,
      settleUploadBatch,
    ]
  );
  const contextValue = useMemo<UploadsState>(
    () => ({
      files,
      ...uploadActions,
    }),
    [files, uploadActions]
  );
  const activeUploadResourceIdsKey = Array.from(
    new Set(
      files
        .filter(isActiveUpload)
        .map(fileInfo => fileInfo.resourceId)
        .filter((resourceId): resourceId is string => !!resourceId)
    )
  )
    .sort()
    .join(',');
  const activeUploadResourceIds = useMemo(
    () =>
      new Set(
        activeUploadResourceIdsKey ? activeUploadResourceIdsKey.split(',') : []
      ),
    [activeUploadResourceIdsKey]
  );

  return (
    <UploadActionsContext.Provider value={uploadActions}>
      <UploadResourceRevisionsContext.Provider value={uploadResourceRevisions}>
        <ActiveUploadResourcesContext.Provider value={activeUploadResourceIds}>
          <UploadsContext.Provider value={contextValue}>
            {children}
          </UploadsContext.Provider>
        </ActiveUploadResourcesContext.Provider>
      </UploadResourceRevisionsContext.Provider>
    </UploadActionsContext.Provider>
  );
};

export default UploadsContextProvider;

export const useUploadsContext = () => {
  const context = useContext(UploadsContext);

  if (!context) {
    throw new Error(
      'useUploadsContext must be used within a UploadsContextProvider'
    );
  }

  return context;
};

export const useUploadActions = () => {
  const context = useContext(UploadActionsContext);

  if (!context) {
    throw new Error(
      'useUploadActions must be used within a UploadsContextProvider'
    );
  }

  return context;
};

export const useActiveUploadResourceIds = () => {
  const context = useContext(ActiveUploadResourcesContext);

  if (!context) {
    throw new Error(
      'useActiveUploadResourceIds must be used within a UploadsContextProvider'
    );
  }

  return context;
};

export const useHasActiveUploads = (resourceId?: string) => {
  const activeUploadResourceIds = useActiveUploadResourceIds();
  return !!resourceId && activeUploadResourceIds.has(resourceId);
};

export const useUploadResourceRevision = (resourceId?: string) => {
  const context = useContext(UploadResourceRevisionsContext);

  if (!context) {
    throw new Error(
      'useUploadResourceRevision must be used within a UploadsContextProvider'
    );
  }

  return resourceId ? context.get(resourceId) ?? 0 : 0;
};
